import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CaretLeftIcon,
  CaretRightIcon,
  MagnifyingGlassIcon,
  StorefrontIcon,
  WarningIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { listMarkets, type MarketSummary } from '../api';
import { useT, type TFunction } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { MARKET_CATEGORIES } from '../lib/market-categories';
import { tryGetTelegram } from '../telegram';

const PAGE_SIZE = 10;

type Mode =
  | { kind: 'cat'; catKey: string; emoji: string; label: string; tagSlug?: string; query: string }
  | { kind: 'search'; term: string };

function fmtVolume(n: number | null, t: TFunction): string {
  if (n === null || !Number.isFinite(n) || n <= 0) return t('market.list.no_volume');
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function fmtPriceCents(p: number): string {
  return `${Math.round(p * 100)}\u00a2`;
}

function fmtEnds(iso: string | null, t: TFunction): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return t('market.list.ends', { date });
}

export default function MarketList() {
  const t = useT();
  const navigate = useNavigate();
  const params = useParams<{ catKey?: string; q?: string }>();

  const mode: Mode | null = useMemo(() => {
    if (params.catKey) {
      const cat = MARKET_CATEGORIES.find((c) => c.key === params.catKey);
      if (!cat) return null;
      return {
        kind: 'cat',
        catKey: cat.key,
        emoji: cat.emoji,
        label: t(`market.cat.${cat.key}`),
        tagSlug: cat.tagSlug,
        query: cat.query,
      };
    }
    if (params.q) {
      return { kind: 'search', term: decodeURIComponent(params.q) };
    }
    return null;
    // i18n function is stable per locale; safe to depend on it.
  }, [params.catKey, params.q, t]);

  const breadcrumbLabel = useMemo(() => {
    if (!mode) return t('market.breadcrumb');
    if (mode.kind === 'cat')
      return t('market.list.breadcrumb_cat', { category: mode.label });
    return t('market.list.breadcrumb_search', { term: mode.term });
  }, [mode, t]);

  useTelegramBackButton();
  useNavStackEntry({ label: breadcrumbLabel });

  const [page, setPage] = useState(0);
  const [items, setItems] = useState<MarketSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPage(0);
  }, [params.catKey, params.q]);

  useEffect(() => {
    if (!mode) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const offset = page * PAGE_SIZE;
        const data =
          mode.kind === 'cat'
            ? await listMarkets({
                tag_slug: mode.tagSlug,
                search: mode.tagSlug ? undefined : mode.query,
                limit: PAGE_SIZE,
                offset,
                order: 'volume24hr',
              })
            : await listMarkets({
                search: mode.term,
                limit: PAGE_SIZE,
                offset,
                order: 'volume24hr',
              });
        if (!cancelled) setItems(data);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, page]);

  if (!mode) {
    return (
      <ScreenShell
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <StorefrontIcon weight="fill" className="size-3" />
            {t('market.eyebrow')}
          </span>
        }
        title={t('market.title')}
      >
        <NavBar />
        <Alert tone="warning">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('market.list.empty_title')}</AlertTitle>
          <AlertDescription>
            <Link to="/markets" className="underline">
              {t('market.list.cta_categories')}
            </Link>
          </AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  if (loading && items === null) {
    return (
      <AuthLoadingScreen
        title={t('market.list.loading_title')}
        description={t('market.list.loading_desc')}
        shortDescription={t('market.list.loading_short')}
        steps={[
          { label: t('market.eyebrow'), color: 'bg-primary' },
          { label: 'Polymarket', color: 'bg-sky-500', delay: '0.4s' },
        ]}
      />
    );
  }

  const eyebrowLabel =
    mode.kind === 'cat'
      ? t('market.list.eyebrow_cat', { category: mode.label })
      : t('market.list.eyebrow_search');
  const titleLabel =
    mode.kind === 'cat'
      ? t('market.list.title_cat', { category: mode.label })
      : t('market.list.title_search', { term: mode.term });
  const descriptionLabel =
    mode.kind === 'cat'
      ? t('market.list.description_cat')
      : t('market.list.description_search');

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          {mode.kind === 'cat' ? (
            <span aria-hidden>{mode.emoji}</span>
          ) : (
            <MagnifyingGlassIcon weight="fill" className="size-3" />
          )}
          {eyebrowLabel}
        </span>
      }
      title={titleLabel}
      description={descriptionLabel}
    >
      <NavBar />

      {error ? (
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('market.list.error_title')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : items && items.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('market.list.empty_title')}</CardTitle>
            <CardDescription>
              {mode.kind === 'cat'
                ? t('market.list.empty_cat')
                : t('market.list.empty_search', { term: mode.term })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/markets" className="block">
              <Button size="lg" variant="outline" className="w-full">
                {t('market.list.cta_categories')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {(items ?? []).map((m) => (
            <MarketCard key={m.slug} market={m} t={t} navigate={navigate} />
          ))}
        </div>
      )}

      {items && items.length > 0 && (
        <Pagination
          page={page}
          hasNext={items.length === PAGE_SIZE}
          onPrev={() => {
            tryGetTelegram()?.HapticFeedback?.impactOccurred?.('light');
            setPage((p) => Math.max(0, p - 1));
          }}
          onNext={() => {
            tryGetTelegram()?.HapticFeedback?.impactOccurred?.('light');
            setPage((p) => p + 1);
          }}
          t={t}
        />
      )}
    </ScreenShell>
  );
}

function MarketCard({
  market,
  t,
  navigate,
}: {
  market: MarketSummary;
  t: TFunction;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const ends = fmtEnds(market.endDate, t);
  function go() {
    tryGetTelegram()?.HapticFeedback?.impactOccurred?.('light');
    navigate(`/markets/m/${encodeURIComponent(market.slug)}`);
  }
  return (
    <button
      type="button"
      onClick={go}
      className="group bg-card/85 supports-backdrop-filter:bg-card/80 ring-foreground/10 hover:ring-primary/30 hover:bg-card relative flex w-full flex-col gap-3 overflow-hidden rounded-3xl p-4 text-left ring-1 backdrop-blur-md transition-all hover:-translate-y-0.5 sm:p-5"
    >
      <div className="flex items-start gap-3">
        {market.icon ?? market.image ? (
          <img
            src={(market.icon ?? market.image) as string}
            alt=""
            loading="lazy"
            className="size-10 shrink-0 rounded-xl object-cover ring-1 ring-foreground/10"
          />
        ) : (
          <span className="bg-primary/12 ring-primary/25 inline-flex size-10 shrink-0 items-center justify-center rounded-xl ring-1">
            <StorefrontIcon weight="fill" className="text-primary size-5" />
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 className="text-foreground text-[15px] font-semibold leading-snug tracking-tight">
            {market.question}
          </h3>
          <p className="text-muted-foreground text-[11px] tabular-nums">
            {t('market.list.volume24h')}: {fmtVolume(market.volume24hr, t)}
            {ends ? <span className="mx-1.5">·</span> : null}
            {ends}
          </p>
        </div>
      </div>

      {market.outcomes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {market.outcomes.slice(0, 4).map((o) => (
            <span
              key={o.tokenId}
              className="bg-primary/10 ring-primary/20 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
            >
              <span className="text-foreground">{o.label}</span>
              <span className="text-primary tabular-nums">
                {fmtPriceCents(o.price)}
              </span>
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

function Pagination({
  page,
  hasNext,
  onPrev,
  onNext,
  t,
}: {
  page: number;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  t: TFunction;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onPrev}
        disabled={page === 0}
      >
        <CaretLeftIcon weight="bold" className="size-4" />
        {t('market.list.prev')}
      </Button>
      <span className="text-muted-foreground text-[11px] font-semibold tabular-nums tracking-[0.14em] uppercase">
        {/* Total page count is unknown — Gamma doesn't return it; we render only the current index. */}
        {`#${page + 1}`}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onNext}
        disabled={!hasNext}
      >
        {t('market.list.next')}
        <CaretRightIcon weight="bold" className="size-4" />
      </Button>
    </div>
  );
}
